import { Inject, Injectable, PLATFORM_ID, Renderer2 } from '@angular/core';
import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import {
  BrowserAbstractionService,
  GoogleAnalyticsService,
} from '@nx-averycom/shared/utils';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  ACG,
  ACGConfigCollection,
  ACGConfigResults,
  StencilWithTemplateDto,
} from '@nx-averycom/shared/avry-models';
import { ApolloQueryResult } from '@apollo/client';
import { GetACGConfigQuery } from './graphql';
import isEmpty from 'lodash-es/isEmpty';
import { Environment, ENVIRONMENT } from '@nx-averycom/environment';

import * as bowser from 'bowser';
import { HttpClient, HttpParams } from '@angular/common/http';
import { WindowService } from '@nx-averycom/shared/browser';

@Injectable({
  providedIn: 'root',
})
export class AppSharedService {
  viewType = new BehaviorSubject<string>('list');
  detectBrowser: bowser.IBowser = bowser;
  themeMode = new BehaviorSubject<string>('');

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    @Inject(ENVIRONMENT) private environment: Environment,
    @Inject(PLATFORM_ID) private platformId: any,
    private apollo: Apollo,
    private browserService: BrowserAbstractionService,
    private http: HttpClient,
    private location: Location,
    private sanitizer: DomSanitizer,
    private titleService: Title,
    private googleService: GoogleAnalyticsService,
    private windowService: WindowService
  ) {}

  /**
   * Sets a dynamic title. Also emits a data layer push event for GTM
   *
   * @param {string} [title='Avery']
   * @param {boolean} [pushDataLayer=true]
   * @memberof AppService
   */
  setTitle(title: string = 'Avery', pushDataLayer: boolean = true) {
    this.titleService.setTitle(title);
    if (pushDataLayer && isPlatformBrowser(this.platformId)) {
      this.googleService.emitCoreDataLayer();
    }
  }

  /**
   *
   * @param names
   * @param sanitize
   */
  getAcgConfigs(
    names: string[],
    sanitize: boolean = true
  ): Observable<ACGConfigResults<SafeHtml>> {
    return this.apollo
      .query<ACGConfigCollection>({
        query: GetACGConfigQuery,
        variables: {
          query: `+AveryCustomGenericConfig.name:"${names.join('" "')}"`,
        },
      })
      .pipe(
        map(({ data }: ApolloQueryResult<ACGConfigCollection>) => {
          if (sanitize) {
            return {
              ...data.results[0],
              acgs: data.results[0].acgs.map((acg: ACG<string>) => {
                return {
                  ...acg,
                  body: this.sanitizeContent(acg.body),
                };
              }),
            };
          }
          return data.results[0];
        })
      );
  }

  /**
   * Sanitize HTML Content
   *
   * @param {string} data
   * @returns {SafeHtml}
   * @memberof AppSharedService
   */
  sanitizeContent(data: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(unescape(data));
  }

  /**
   * Function to check valid value for a numeric only keyboard.
   *
   * @param {event}
   * @return {boolean}
   * @memberof AppService
   */
  numericKeyboard(event: KeyboardEvent): boolean {
    // NOTE: Valid Keys are: 0~9 (keycodes: 48~57 and 96~105[windows numberpad]), left and right arrow keys, shift, caps-lock, backspace, and delete keys
    // NOTE: `event.key` is the recomended property but some browsers might still not support this property. `event.keyIdentifier` or `event.keyCode` will be the fall back.
    if (event.key) {
      return (
        (Number.isFinite(Number(event.key)) && event.key !== ' ') ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Backspace' ||
        event.key === 'CapsLock' ||
        event.key === 'Del' ||
        event.key === 'Shift' ||
        event.key === 'Tab'
      );
    } else if (event.keyCode) {
      return (
        (Number.isFinite(Number(String.fromCharCode(event.keyCode))) &&
          event.keyCode !== 32) ||
        (event.keyCode >= 96 && event.keyCode <= 105) ||
        event.keyCode === 8 || // backspace key
        event.keyCode === 9 || // Tab key
        event.keyCode === 16 || // shift key
        event.keyCode === 20 || // capslock key
        event.keyCode === 37 || // left arrow
        event.keyCode === 39 || // right arrow
        event.keyCode === 46
      ); // delete key
    }
    return false;
  }

  /**
   *
   */
  getBreakPoint() {
    const window = this.windowService.getWindow();

    if (window) {
      const breakpointContent = window
        .getComputedStyle(this._document.body, ':before')
        .getPropertyValue('content')
        .replace(/["|']/g, '')
        .split(':');
      const result = [];
      if (breakpointContent.length > 1) {
        const breakpointLabel = breakpointContent[0];
        const breakpoint = Number(breakpointContent[1].replace('px', ''));
        result.push(breakpointContent[0]);
        result.push(breakpointContent[1]);
      }
      return result;
    }
    return [];
  }

  isMobile() {
    const breakpoint = this.getBreakPoint() || [];

    if (
      breakpoint.length > 0 &&
      (breakpoint[0] === 'xs' || breakpoint[0] === 'sm')
    ) {
      return true;
    }

    return false;
  }
  /**
   * Return specific breakpoint
   *
   * @memberof NavigationService
   */
  isTablet() {
    const breakpoint = this.getBreakPoint() || [];
    if (
      breakpoint.length > 0 &&
      (breakpoint[0] === 'xs' ||
        breakpoint[0] === 'sm' ||
        breakpoint[0] === 'md')
    ) {
      return true;
    }
    return false;
  }

  /**
   * Function that returns an object after taking out empty properties. Used to reduce filter
   * cluttering in the URL
   *
   * @param object
   */
  removeEmptyProperties(object: any) {
    const newObject = object;
    const keys = Object.keys(newObject);

    keys.forEach((key) => {
      if (isEmpty(newObject[key])) {
        delete newObject[key];
      }
    });

    return newObject;
  }

  /**
   * Function that modifies a string to make it readable to the browser by preventing weird encoding
   * characters. Also makes it readable for human eyes too! It is important to keep in mind that order
   * of the if logic matters (mostly because of the dashes)
   *
   * @export
   * @param {string} [snippet='']
   * @returns {string}
   */
  toBrowser(snippet: string = ''): string {
    return snippet
      .toLowerCase()
      .replace(/&#44;/g, '__')
      .replace(/-/g, '_')
      .replace(/\//g, '--')
      .replace(/ /g, '-')
      .replace(/&/g, '~');
  }

  /**
   * Change the view to display grid or list
   *
   * @param {string} type
   * @memberof AppService
   */
  changeView(type: string) {
    this.viewType.next(type);
    const config = this.browserService.localStorageGetter('filterConfig') || {
      viewType: '',
    };
    config.viewType = type;
    this.browserService.localStorageSetter('filterConfig', config);
  }
  /**
   * Set Canonical URL using the parameters passed here.
   *
   * @param {Renderer2} renderer
   * @param {string} [type='default']
   * @param {boolean} [insertBefore=true]
   * @param {string} [preformattedPath=null]
   * @param {string} [preset=null]
   * @param {string} [format=null]
   * @returns
   * @memberof AppService
   */
  addCanonicalURL(
    renderer: Renderer2,
    type: string = 'default',
    insertBefore: boolean = true,
    preformattedPath: string = '',
    preset: any = null,
    format: any = null
  ) {
    let directory, parameters;

    if (this.location.path().includes('?')) {
      [directory, parameters] = this.location.path().split('?');
    } else {
      directory = this.location.path();
    }

    let url =
      preformattedPath !== ''
        ? `${this.environment.domain}${preformattedPath}`
        : `${this.environment.domain}${directory}`;

    if (type === 'default' || isEmpty(type)) {
      // Replaces /industrial with empty string
      url = url.replace(/\/\b(industrial)\b/gi, '');
    } else {
      // For industrial product or template type, if url has no /industrial, append it.
      if (this.location.path().indexOf('/industrial') === -1) {
        url = `${this.environment.domain}/industrial${this.location.path()}`;
      }
    }

    if (parameters) {
      parameters.split('&').forEach((param) => {
        if (param.split('=')[0] === 'page') {
          url = url + '?' + param;
          return;
        }
      });
    }

    // url for default calculator keeps /sheets/, /rolls/, /stickers/
    // Removes `/sheets/` or `/rolls/` or `/stickers/` for preset calculator
    !preset && format
      ? url
      : (url = url.replace(/\/(\bsheets|rolls|stickers\b)\/?$/g, ''));

    // Remove trailing question marks
    url = url.replace(/(\?)+$/, '');

    const canonicalURLElement = renderer.createElement('link');
    const head = this._document.head;

    // Removes previously added canonical url before adding a new one to avoid duplicate
    this._document
      .querySelectorAll('link[rel="canonical"]')
      .forEach((link: any) => {
        renderer.removeChild(head, link);
      });

    renderer.setAttribute(canonicalURLElement, 'rel', 'canonical');
    renderer.setAttribute(canonicalURLElement, 'href', url);
    if (insertBefore) {
      renderer.insertBefore(head, canonicalURLElement, head.firstChild);
    } else {
      renderer.appendChild(head, canonicalURLElement);
    }

    return canonicalURLElement;
  }

  /**
   * Checks if Browser is Internet Explorer
   *
   * @memberof AppService
   */
  checkForIE() {
    return this.detectBrowser.name === 'Internet Explorer';
  }

  /**
   * Gets the client Browser Name
   *
   * @returns
   * @memberof NavigationService
   */
  getClientBrowserName() {
    return this.detectBrowser.name;
  }

  /**
   * Fetches ACG (Avery Custom Generic) data from dotCMS API.
   *
   * @param {string} acg
   * @returns {Observable<any>}
   * @memberof NavigationService
   */
  fetchGeneralModalData(type: string, url: string): Observable<ACG<string>> {
    return this.http.get<ACG<string>>(
      `${this.environment.domain}/rest/custom/acg/${type}/${url}`
    );
  }

  /**
   * Dpo service to get custom size templateId
   * @param {HttpParams} params
   * @return {Observable<any>}
   * @memberof NavigationService
   */
  getCustomTemplateId(
    params: HttpParams
  ): Observable<StencilWithTemplateDto[]> {
    const deploymentId = this.environment.deploymentId;
    params = params
      .append('consumer', 'Avery')
      .append('deploymentId', deploymentId);
    return this.http.get<StencilWithTemplateDto[]>(
      `${this.environment.dpo}/${this.environment.dpoV4}/template-ids/search`,
      { params }
    );
  }

  /**
   * Sets the themeMode for the BS
   *
   * @memberof NavigationService
   */
  async setThemeMode() {
    const theme = await this.browserService.checkAppThemeMode();
    this.themeMode.next(theme);
  }
}
